import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Package, Wrench, MapPin, Clock, X, Plus, Search, Tag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import api from '@/lib/api';
import { toast } from 'sonner';

const conditions = [
  { value:'nuevo', label:'Nuevo' }, { value:'como_nuevo', label:'Como nuevo' },
  { value:'bueno', label:'Buen estado' }, { value:'usado', label:'Usado' },
];
const locationTypes = [
  { value:'store', label:'Local Establecido' },
  { value:'home', label:'A Domicilio' },
  { value:'digital', label:'Digital / Remoto' },
];
const locationOptions = ['Ocoyoacac','Toluca','Metepec','Lerma','CDMX','Otro'];

const getMediaUrl = (url) => {
  if (!url) return '';
  return url.startsWith('http') ? url : `${url}`;
};

export default function EditPage() {
  const { itemId } = useParams();
  const navigate = useNavigate();
  const imgRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState('');
  const [price, setPrice] = useState('');
  const [description, setDescription] = useState('');
  const [condition, setCondition] = useState('bueno');
  const [itemType, setItemType] = useState('product');
  const [locationType, setLocationType] = useState('');
  const [serviceAddress, setServiceAddress] = useState('');
  const [serviceHours, setServiceHours] = useState('');
  const [location, setLocation] = useState('');
  const [isSold, setIsSold] = useState(false);
  const [isClosed, setIsClosed] = useState(false);
  const [discountPrice, setDiscountPrice] = useState('');

  // Image state: existing URLs to keep + new files to upload
  const [existingImages, setExistingImages] = useState([]);
  const [newImageFiles, setNewImageFiles] = useState([]);
  const [newImagePreviews, setNewImagePreviews] = useState([]);

  useEffect(() => {
    api.get(`/items/${itemId}`).then(r => {
      const d = r.data;
      setTitle(d.title || '');
      setPrice(String(d.price || ''));
      setDescription(d.description || '');
      setCondition(d.condition || 'bueno');
      setItemType(d.item_type || 'product');
      setLocationType(d.service_location_type || '');
      setServiceAddress(d.service_address || '');
      setServiceHours(d.service_hours || '');
      setLocation(d.location || '');
      setIsSold(d.is_sold || d.status === 'sold' || false);
      setIsClosed(d.is_closed || false);
      setDiscountPrice(d.discount_price ? String(d.discount_price) : '');
      setExistingImages(d.images || []);
      setLoading(false);
    }).catch(() => { toast.error('Error al cargar'); navigate(-1); });
  }, [itemId, navigate]);

  const removeExisting = (idx) => setExistingImages(prev => prev.filter((_, i) => i !== idx));
  const removeNew = (idx) => {
    setNewImageFiles(prev => prev.filter((_, i) => i !== idx));
    setNewImagePreviews(prev => prev.filter((_, i) => i !== idx));
  };
  const handleNewImages = (e) => {
    const files = Array.from(e.target.files || []);
    const total = existingImages.length + newImageFiles.length + files.length;
    if (total > 5) { toast.error('Maximo 5 imagenes en total'); return; }
    setNewImageFiles(prev => [...prev, ...files]);
    setNewImagePreviews(prev => [...prev, ...files.map(f => URL.createObjectURL(f))]);
  };

  const totalImages = existingImages.length + newImageFiles.length;

  const handleSave = async (e) => {
    e.preventDefault();
    if (!title || !price) { toast.error('Titulo y precio obligatorios'); return; }
    setSaving(true);
    const fd = new FormData();
    fd.append('title', title);
    fd.append('price', parseFloat(price));
    fd.append('description', description);
    fd.append('item_type', itemType);
    fd.append('condition', itemType === 'product' ? condition : '');
    fd.append('service_location_type', itemType === 'service' ? locationType : '');
    fd.append('service_address', itemType === 'service' ? serviceAddress : '');
    fd.append('service_hours', itemType === 'service' ? serviceHours : '');
    fd.append('location', location);
    fd.append('is_sold', isSold ? 'true' : 'false');
    fd.append('is_closed', isClosed ? 'true' : 'false');
    fd.append('discount_price', discountPrice || '0');
    fd.append('keep_images', JSON.stringify(existingImages));
    newImageFiles.forEach(f => fd.append('images', f));
    try {
      await api.put(`/items/${itemId}`, fd);
      toast.success('Actualizado!');
      navigate(`/item/${itemId}`);
    } catch { toast.error('Error al guardar'); } finally { setSaving(false); }
  };

  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  const isSvc = itemType === 'service';
  const isWanted = itemType === 'wanted';

  return (
    <div data-testid="edit-page" className="min-h-screen bg-background page-with-nav">
      <div className="max-w-md mx-auto px-4">
        <div className="flex items-center gap-3 mb-6">
          <button data-testid="edit-back-btn" onClick={() => navigate(-1)}><ArrowLeft className="w-6 h-6 text-foreground" /></button>
          <h1 className="font-heading text-2xl text-primary uppercase">EDITAR</h1>
        </div>
        <form onSubmit={handleSave} className="space-y-5">
          {/* Type toggle */}
          <div className="grid grid-cols-3 gap-2">
            <button type="button" data-testid="edit-type-product" onClick={() => setItemType('product')}
              className={`flex items-center justify-center gap-1.5 px-3 py-3 rounded-sm border-2 font-body font-bold text-xs transition-all ${itemType === 'product' ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground'}`}>
              <Package className="w-4 h-4" /> Producto
            </button>
            <button type="button" data-testid="edit-type-service" onClick={() => setItemType('service')}
              className={`flex items-center justify-center gap-1.5 px-3 py-3 rounded-sm border-2 font-body font-bold text-xs transition-all ${isSvc ? 'border-orange-400 bg-orange-400/10 text-orange-400' : 'border-border text-muted-foreground'}`}>
              <Wrench className="w-4 h-4" /> Servicio
            </button>
            <button type="button" data-testid="edit-type-wanted" onClick={() => setItemType('wanted')}
              className={`flex items-center justify-center gap-1.5 px-3 py-3 rounded-sm border-2 font-body font-bold text-xs transition-all ${isWanted ? 'border-[#FFD700] bg-[#FFD700]/10 text-[#FFD700]' : 'border-border text-muted-foreground'}`}>
              <Search className="w-4 h-4" /> Se Busca
            </button>
          </div>

          {/* Image management */}
          <div className="space-y-2">
            <label className="font-body font-medium text-foreground text-sm">
              {isWanted ? 'Fotos de referencia (opcional)' : 'Fotos'} ({totalImages}/5)
            </label>
            <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-2">
              {/* Existing images */}
              {existingImages.map((url, i) => (
                <div key={`ex-${i}`} className="w-24 h-24 rounded-sm overflow-hidden bg-zinc-900 border-2 border-border shrink-0 relative">
                  <img src={getMediaUrl(url)} className="object-cover w-full h-full" alt="" />
                  <button type="button" data-testid={`edit-remove-existing-${i}`} onClick={() => removeExisting(i)}
                    className="absolute top-1 right-1 bg-black/80 p-0.5 rounded-sm">
                    <X className="w-3 h-3 text-white" />
                  </button>
                </div>
              ))}
              {/* New image previews */}
              {newImagePreviews.map((p, i) => (
                <div key={`new-${i}`} className="w-24 h-24 rounded-sm overflow-hidden bg-zinc-900 border-2 border-primary/50 shrink-0 relative">
                  <img src={p} className="object-cover w-full h-full" alt="" />
                  <button type="button" data-testid={`edit-remove-new-${i}`} onClick={() => removeNew(i)}
                    className="absolute top-1 right-1 bg-black/80 p-0.5 rounded-sm">
                    <X className="w-3 h-3 text-white" />
                  </button>
                </div>
              ))}
              {/* Add button */}
              {totalImages < 5 && (
                <button type="button" data-testid="edit-add-image-btn" onClick={() => imgRef.current?.click()}
                  className="w-24 h-24 rounded-sm border-2 border-dashed border-border flex flex-col items-center justify-center gap-1 shrink-0 hover:border-primary transition-colors">
                  <Plus className="w-5 h-5 text-muted-foreground" />
                  <span className="text-muted-foreground text-[10px] font-body">Foto</span>
                </button>
              )}
            </div>
            <input ref={imgRef} type="file" accept="image/*" multiple onChange={handleNewImages} className="hidden" />
          </div>

          {/* Title */}
          <div className="space-y-2">
            <label className="font-body font-medium text-foreground text-sm">{isWanted ? 'Que buscas?' : 'Titulo'}</label>
            <Input data-testid="edit-title" value={title} onChange={e => setTitle(e.target.value)} className="rounded-sm border-2 bg-card font-body h-12" />
          </div>

          {/* Price */}
          <div className="space-y-2">
            <label className="font-body font-medium text-foreground text-sm">{isWanted ? 'Presupuesto (MXN)' : isSvc ? 'Costo Base (MXN)' : 'Precio (MXN)'}</label>
            <Input data-testid="edit-price" type="number" value={price} onChange={e => setPrice(e.target.value)} className="rounded-sm border-2 bg-card font-body h-12" />
          </div>

          {/* Discount Price */}
          {!isWanted && (
            <div className="space-y-2">
              <label className="font-body font-medium text-foreground text-sm flex items-center gap-2">
                <Tag className="w-4 h-4 text-red-400" /> Precio con descuento (MXN) <span className="text-muted-foreground text-[10px] font-normal">Opcional</span>
              </label>
              <Input data-testid="edit-discount-price" type="number" value={discountPrice}
                onChange={e => setDiscountPrice(e.target.value)}
                placeholder="Dejar vacio si no hay descuento"
                className="rounded-sm border-2 bg-card font-body h-12" />
              {discountPrice && parseFloat(discountPrice) > 0 && parseFloat(price) > 0 && (
                <p className="text-xs font-body" style={{ color: '#555' }}>
                  Se mostrara: <span style={{ textDecoration: 'line-through', color: '#666' }}>${parseFloat(price).toLocaleString()}</span>{' '}
                  <span style={{ color: '#ef4444', fontWeight: 700 }}>${parseFloat(discountPrice).toLocaleString()}</span>{' '}
                  ({Math.round((1 - parseFloat(discountPrice) / parseFloat(price)) * 100)}% OFF)
                </p>
              )}
            </div>
          )}

          {/* Sold / Closed Toggle — contextual per item type */}
          {isSvc ? (
            <button type="button" data-testid="edit-closed-toggle"
              onClick={() => setIsClosed(!isClosed)}
              className="w-full flex items-center justify-between rounded-xl px-4 py-3"
              style={{ background: '#0f0f0f', border: isClosed ? '1px solid #f9731640' : '1px solid #1a1a1a' }}>
              <div className="text-left">
                <p className="font-body font-semibold text-sm" style={{ color: isClosed ? '#f97316' : '#d5d5d5' }}>Cerrado por ahora</p>
                <p className="font-body text-[10px]" style={{ color: '#555' }}>Tu negocio no esta disponible ahorita</p>
              </div>
              <div className="w-11 h-6 rounded-full p-0.5 transition-colors shrink-0"
                style={{ background: isClosed ? '#f97316' : '#333' }}>
                <div className="w-5 h-5 rounded-full bg-white shadow transition-transform"
                  style={{ transform: isClosed ? 'translateX(20px)' : 'translateX(0)' }} />
              </div>
            </button>
          ) : (
            <button type="button" data-testid="edit-sold-toggle"
              onClick={() => setIsSold(!isSold)}
              className="w-full flex items-center justify-between rounded-xl px-4 py-3"
              style={{ background: '#0f0f0f', border: isSold ? '1px solid #ef444440' : '1px solid #1a1a1a' }}>
              <div className="text-left">
                <p className="font-body font-semibold text-sm" style={{ color: isSold ? '#ef4444' : '#d5d5d5' }}>Marcar como Vendido</p>
                <p className="font-body text-[10px]" style={{ color: '#555' }}>Se mostrara como no disponible</p>
              </div>
              <div className="w-11 h-6 rounded-full p-0.5 transition-colors shrink-0"
                style={{ background: isSold ? '#ef4444' : '#333' }}>
                <div className="w-5 h-5 rounded-full bg-white shadow transition-transform"
                  style={{ transform: isSold ? 'translateX(20px)' : 'translateX(0)' }} />
              </div>
            </button>
          )}

          {/* Location */}
          <div className="space-y-2">
            <label className="font-body font-medium text-foreground text-sm flex items-center gap-2"><MapPin className="w-4 h-4 text-primary" /> Ubicacion</label>
            <div className="flex flex-wrap gap-2">
              {locationOptions.map(loc => (
                <button key={loc} type="button" onClick={() => setLocation(location === loc ? '' : loc)}
                  className={`px-3 py-2 rounded-sm border-2 font-body text-sm transition-colors ${location===loc ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground'}`}>
                  {loc}
                </button>
              ))}
            </div>
          </div>

          {/* Condition — products only */}
          {!isSvc && !isWanted && (
            <div className="space-y-2">
              <label className="font-body font-medium text-foreground text-sm">Condicion</label>
              <div className="flex flex-wrap gap-2">
                {conditions.map(c => (
                  <button key={c.value} type="button" onClick={() => setCondition(c.value)}
                    className={`px-3 py-2 rounded-sm border-2 font-body text-sm ${condition===c.value ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground'}`}>
                    {c.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Service fields */}
          {isSvc && (
            <>
              <div className="space-y-2">
                <label className="font-body font-medium text-foreground text-sm">Tipo de Ubicacion</label>
                <div className="flex flex-wrap gap-2">
                  {locationTypes.map(lt => (
                    <button key={lt.value} type="button" onClick={() => setLocationType(lt.value)}
                      className={`px-3 py-2 rounded-sm border-2 font-body text-sm ${locationType===lt.value ? 'border-orange-400 bg-orange-400/10 text-orange-400' : 'border-border text-muted-foreground'}`}>
                      {lt.label}
                    </button>
                  ))}
                </div>
              </div>
              {locationType === 'store' && (
                <div className="space-y-2">
                  <label className="font-body font-medium text-foreground text-sm flex items-center gap-2"><MapPin className="w-4 h-4 text-orange-400" /> Direccion</label>
                  <Input data-testid="edit-address" value={serviceAddress} onChange={e => setServiceAddress(e.target.value)} className="rounded-sm border-2 bg-card font-body h-12" />
                </div>
              )}
              <div className="space-y-2">
                <label className="font-body font-medium text-foreground text-sm flex items-center gap-2"><Clock className="w-4 h-4 text-orange-400" /> Horario</label>
                <Input data-testid="edit-hours" value={serviceHours} onChange={e => setServiceHours(e.target.value)} className="rounded-sm border-2 bg-card font-body h-12" />
              </div>
            </>
          )}

          {/* Description */}
          <div className="space-y-2">
            <label className="font-body font-medium text-foreground text-sm">Descripcion</label>
            <textarea data-testid="edit-description" value={description} onChange={e => setDescription(e.target.value)} rows={3}
              className="w-full rounded-sm border-2 border-border bg-card font-body text-foreground p-3 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-primary" />
          </div>

          <Button data-testid="edit-save-btn" type="submit" disabled={saving || !title || !price}
            className="w-full h-14 rounded-sm font-body font-bold uppercase text-base bg-primary text-primary-foreground hover:bg-primary/90">
            {saving ? 'GUARDANDO...' : 'GUARDAR CAMBIOS'}
          </Button>
        </form>
      </div>
    </div>
  );
}
